import React from 'react';
import { CHART_RAMP } from '../../design/tokens';
import { useCurrency } from '../../context/CurrencyContext';
import useProgressRing from '../../hooks/useProgressRing';
import VisionQuotaIndicator from '../upgrade/VisionQuotaIndicator';
import VisionLimitCTA from '../upgrade/VisionLimitCTA';

const RING_SIZE = 72;
const STROKE = 6;
const RADIUS = (RING_SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

const ProgressRing = ({ percent, animate }) => {
  const animated = useProgressRing(percent, 1000, animate);
  const offset = CIRC - (animated / 100) * CIRC;

  return (
    <svg width={RING_SIZE} height={RING_SIZE} className="shrink-0 drop-shadow-sm" aria-hidden>
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RADIUS}
        fill="none"
        stroke="rgba(255,255,255,0.25)"
        strokeWidth={STROKE}
      />
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RADIUS}
        fill="none"
        stroke={CHART_RAMP[0]}
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeDasharray={CIRC}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
        className="transition-all duration-300"
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        className="fill-white font-display font-semibold"
        style={{ fontSize: '0.95rem' }}
      >
        {Math.round(animated)}%
      </text>
    </svg>
  );
};

const VisionImageCard = ({ vision, onSelect, formatAmount, animate }) => {
  const pct = Math.min(100, Number(vision.progress_pct) || 0);
  const target = Number(vision.target_amount) || 0;
  const current = Number(vision.current_saved) || 0;
  const remaining = Math.max(0, target - current);
  const imageUrl = vision.image_url;
  const animatedBar = useProgressRing(pct, 1000, animate);

  return (
    <button
      type="button"
      onClick={() => onSelect(vision)}
      className="vision-image-card group w-full text-left rounded-xl overflow-hidden shadow-elevated hover:shadow-elevated-lg transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-pine"
    >
      <div
        className="relative min-h-[200px] md:min-h-[220px] flex flex-col justify-end p-5"
        style={
          imageUrl
            ? {
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : {
                background: 'linear-gradient(135deg, var(--pine-deep) 0%, var(--pine) 55%, #2d5a4a 100%)',
              }
        }
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/10" />
        <div className="relative z-10 flex items-end justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-body font-medium text-white text-lg mb-2 truncate">
              {vision.emoji || '🎯'} {vision.name}
            </p>
            <div className="h-1.5 rounded-full bg-white/20 overflow-hidden mb-2 max-w-xs">
              <div
                className="h-full rounded-full bg-pine-tint transition-all duration-1000 ease-out"
                style={{ width: `${animatedBar}%` }}
              />
            </div>
            <p className="text-label text-white/85">
              {Math.round(pct)}% there · {formatAmount(remaining, { maximumFractionDigits: 0 })} to go
            </p>
            {Number(vision.committed_monthly) > 0 && (
              <span className="inline-block mt-2 text-label px-2.5 py-1 rounded-full bg-white/15 text-white border border-white/20">
                {formatAmount(vision.committed_monthly, { maximumFractionDigits: 0 })}/month
              </span>
            )}
          </div>
          <ProgressRing percent={pct} animate={animate} />
        </div>
      </div>
    </button>
  );
};

const DashboardVisionProgress = ({
  visions = [],
  onCreateVision,
  onVisionSelect,
  atVisionLimit,
  permissions,
  onUpgrade,
  animate = true,
}) => {
  const { formatAmount } = useCurrency();

  return (
    <section className="bento-card" aria-label="Your Visions">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
        <h2 className="font-display text-h2 text-primary">Your Visions</h2>
        {visions.length > 0 && !atVisionLimit && (
          <button
            type="button"
            onClick={onCreateVision}
            className="text-body text-pine font-medium hover:underline inline-flex items-center gap-1"
          >
            + Add Vision
          </button>
        )}
      </div>
      {permissions && <VisionQuotaIndicator permissions={permissions} className="mb-6" />}

      {atVisionLimit && visions.length > 0 && onUpgrade && (
        <div className="mb-6">
          <VisionLimitCTA onUpgrade={onUpgrade} compact />
        </div>
      )}

      {visions.length === 0 ? (
        <div className="p-8 md:p-10 border border-pine/30 rounded-xl text-center">
          <h3 className="font-display text-h2 text-primary mb-3">Start your first Vision</h3>
          <p className="text-body text-secondary max-w-md mx-auto mb-6">
            Tell Aura what you&apos;re saving for and it will build your plan.
          </p>
          {atVisionLimit && onUpgrade ? (
            <div className="max-w-md mx-auto text-left">
              <VisionLimitCTA onUpgrade={onUpgrade} />
            </div>
          ) : (
            <button type="button" onClick={onCreateVision} className="btn-primary">
              Start a Vision
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visions.map((v) => (
            <VisionImageCard
              key={v.vision_id}
              vision={v}
              onSelect={onVisionSelect}
              formatAmount={formatAmount}
              animate={animate}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default DashboardVisionProgress;
